import { API } from '../App';

const DEFAULT_POLL_INTERVAL_MS = 2000;
// #1221: was 60000 (60s), but the backend's markitdown conversion path
// for binary files (PDF/PPTX/DOCX) takes 10-60s by design — at 60s
// frontend timeout, multi-MB uploads race the boundary. The job
// completes server-side but the frontend bails out before polling
// sees status=done, leaving an orphan context (created but never
// attached to the project). Bumped to 180s to give markitdown
// headroom + a margin for network jitter. Markdown / text uploads
// finish in <3s so the longer timeout has zero effect on the fast path.
const DEFAULT_TIMEOUT_MS = 180000;

/**
 * Upload a single File to project- or fray-scope. Returns {context_id}.
 *
 * Pipeline:
 *  1. POST /api/contexts/from-file (multipart) → {job_id}
 *  2. Poll GET /api/contexts/upload-status/{job_id} until status=='done' → {context_id}
 *  3. POST attach to scope (project or fray)
 *
 * scope = { type: 'project', projectId } | { type: 'fray', frayId }
 *
 * #1221: errors thrown from this function carry enough context for the
 * UI's per-file error row to be actionable — file name (caller knows),
 * job_id when available (so support can correlate to the
 * context_upload_jobs row), and the failure phase ('start' / 'poll' /
 * 'attach').
 */
export async function uploadProjectFile(file, scope, options = {}) {
  const pollIntervalMs = options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  // ── Phase 1: start the upload ────────────────────────────────────
  // #1228: send attach intent at upload-time so the worker performs
  // the project_contexts / fray_contexts INSERT atomically with the
  // contexts INSERT. Eliminates the orphan race entirely — there's no
  // longer a frontend window between poll-success and a separate
  // attach-call because there's no separate attach-call.
  const form = new FormData();
  form.append('file', file);
  let backendAttachedAtomically = false;
  if (scope.type === 'project' && scope.projectId) {
    form.append('attach_to_project_id', scope.projectId);
    backendAttachedAtomically = true;
  } else if (scope.type === 'fray' && scope.frayId) {
    form.append('attach_to_fray_id', scope.frayId);
    backendAttachedAtomically = true;
  }
  let startResp;
  try {
    startResp = await API.post('/api/contexts/from-file', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  } catch (e) {
    const detail = e?.response?.data?.detail || e?.message || String(e);
    const err = new Error(`Could not start upload: ${detail}`);
    err.phase = 'start';
    throw err;
  }
  const jobId = startResp?.data?.job_id;
  if (!jobId) {
    const err = new Error('Backend accepted upload but returned no job_id');
    err.phase = 'start';
    throw err;
  }

  // ── Phase 2: poll until conversion finishes ─────────────────────
  // /api/contexts/upload-status returns:
  //   { phase: 'received' | 'uploading_storage' | 'converting' | 'done'
  //            | 'failed',
  //     percent: int,
  //     result_context_id: uuid (set when phase='done'),
  //     error: string (set when phase='failed') }
  //
  // (#1221 follow-up — the actual root cause) Pre-existing
  // uploadProjectFile.js read `data.status` and `data.context_id` —
  // neither field exists in the response. Polling never saw the
  // terminal state, hit the timeout, surfaced a "still processing"
  // error, and the orphan was created (#1228 root cause). The 60s
  // timeout was a symptom, not the disease. Reading the correct field
  // names is the real fix — the backend always worked.
  const start = Date.now();
  let contextId = null;
  let lastPhase = null;
  while (Date.now() - start < timeoutMs) {
    let data;
    try {
      const pollResp = await API.get(`/api/contexts/upload-status/${jobId}`);
      data = pollResp?.data || {};
    } catch (e) {
      // Transient network failure — keep polling rather than abandon.
      data = {};
    }
    lastPhase = data.phase || lastPhase;
    if (data.phase === 'done' && data.result_context_id) {
      contextId = data.result_context_id;
      break;
    }
    if (data.phase === 'failed') {
      const err = new Error(data.error || `Upload failed during ${lastPhase || 'processing'}`);
      err.phase = 'poll';
      err.jobId = jobId;
      throw err;
    }
    await new Promise((r) => setTimeout(r, pollIntervalMs));
  }
  if (!contextId) {
    // Timeout: backend may still be processing. We do NOT cancel the
    // job server-side (next page-load reconcile — TODO) but we report
    // the failure with enough context to recover.
    const err = new Error(
      `Upload timeout after ${timeoutMs / 1000}s ` +
      `(last phase: ${lastPhase || 'unknown'}). The file may still finish on the ` +
      `server — refresh in a minute. job_id=${jobId}`
    );
    err.phase = 'poll-timeout';
    err.jobId = jobId;
    throw err;
  }

  // ── Phase 3: attach to scope ────────────────────────────────────
  // #1228: when the backend attached atomically (we sent attach intent
  // in step 1), there's nothing for us to do here — the project_contexts
  // / fray_contexts row is already in place. Skip step 3 entirely.
  // Legacy 3-step flow only fires for callers that pass an unknown
  // scope shape (defensive — shouldn't happen in practice).
  if (backendAttachedAtomically) {
    return { context_id: contextId };
  }
  try {
    if (scope.type === 'project') {
      await API.post(`/api/projects/${scope.projectId}/contexts`, {
        context_id: contextId,
        role: 'reference',
      });
    } else if (scope.type === 'fray') {
      await API.post(`/api/frays/${scope.frayId}/contexts`, {
        context_id: contextId,
        role: 'reference',
      });
    } else {
      const err = new Error(`Unknown scope type: ${scope.type}`);
      err.phase = 'attach';
      err.contextId = contextId;
      throw err;
    }
  } catch (e) {
    const detail = e?.response?.data?.detail || e?.message || String(e);
    const err = new Error(
      `File converted but attach to ${scope.type} failed: ${detail}. ` +
      `context_id=${contextId} (file is in your context library; you can ` +
      `attach manually).`
    );
    err.phase = 'attach';
    err.contextId = contextId;
    throw err;
  }

  return { context_id: contextId };
}
